import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Clock, MailCheck, ArrowRight, Sparkles } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import SEOHead from '../components/SEOHead';

const API = `${window.location.origin}/api`;

export default function PendingPaymentPage() {
  const { isAuthenticated, user } = useAuth();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('auth_token')}` });

  const fetchStatus = () => {
    if (!isAuthenticated) { setLoading(false); return; }
    axios.get(`${API}/tester-kit/my-order`, { headers: headers() })
      .then(r => {
        const o = r.data?.order;
        setOrder(o);
        if (o?.status === 'approved') {
          navigate('/ambassador');
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchStatus();
    const id = setInterval(fetchStatus, 30000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  if (loading) return <div className="min-h-screen flex items-center justify-center text-white/40 text-sm font-sans">Checking your order…</div>;

  if (!order) {
    return (
      <main className="min-h-screen flex items-center justify-center px-4">
        <SEOHead title="No Order Found" robots="noindex,nofollow" />
        <div className="text-center max-w-md">
          <p className="text-white/60 text-sm font-sans mb-4">You haven&apos;t submitted a Tester Kit payment yet.</p>
          <Link to="/tester-kit-checkout" className="inline-flex items-center gap-2 px-5 py-3 bg-[#D4AF37] text-black uppercase tracking-[0.2em] text-xs font-sans hover:bg-[#E5C158]">
            Pay your Tester Kit <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4 py-12">
      <SEOHead title="Payment Pending" robots="noindex,nofollow" />
      <div className="max-w-xl w-full text-center">
        <div className="relative mb-8 inline-block">
          <Clock className="w-14 h-14 text-[#D4AF37]" strokeWidth={1.3} />
          <Sparkles className="w-5 h-5 text-[#D4AF37] absolute -top-1 -right-2 animate-pulse" />
        </div>
        <p className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37] mb-3 font-sans">Awaiting Confirmation</p>
        <h1 className="text-3xl sm:text-4xl text-white font-light mb-4 leading-tight" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          Salamat, {user?.name?.split(' ')[0] || 'Kapatid'}!<br />
          <span className="text-[#D4AF37] italic">Your payment is being verified.</span>
        </h1>
        <p className="text-white/60 text-sm font-sans max-w-md mx-auto mb-8">
          We received your <span className="text-white">{order.payment_method}</span> proof for ₱{order.package_price?.toLocaleString()}. Owner manually reviews each payment within <strong className="text-white">24 hours</strong> (usually faster).
        </p>

        <div className="bg-[#141414] border border-white/10 p-5 mb-8 text-left">
          <div className="flex justify-between items-center text-xs font-sans mb-2">
            <span className="text-white/40">Status</span>
            <span className="text-[#D4AF37] uppercase tracking-[0.18em]">Pending Approval</span>
          </div>
          <div className="flex justify-between items-center text-xs font-sans mb-2">
            <span className="text-white/40">Submitted</span>
            <span className="text-white">{new Date(order.submitted_at || order.created_at).toLocaleString('en-PH', { dateStyle: 'medium', timeStyle: 'short' })}</span>
          </div>
          {order.payment_reference && (
            <div className="flex justify-between items-center text-xs font-sans">
              <span className="text-white/40">Reference</span>
              <span className="text-white font-mono">{order.payment_reference}</span>
            </div>
          )}
        </div>

        <div className="flex items-start gap-3 text-white/50 text-xs font-sans mb-6 text-left bg-[#1C7D3F]/5 border border-[#1C7D3F]/20 p-4">
          <MailCheck className="w-4 h-4 text-[#1C7D3F] flex-shrink-0 mt-0.5" />
          <p>You&apos;ll get an email + dashboard auto-unlocks the moment we approve. While you wait, follow our <a href="https://www.tiktok.com/@jsfragranceofficial" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] hover:underline">TikTok</a> + <a href="https://www.facebook.com/profile.php?id=61588041885547" target="_blank" rel="noopener noreferrer" className="text-[#D4AF37] hover:underline">Facebook</a> para sa brand voice.</p>
        </div>

        <p className="text-white/30 text-[11px] uppercase tracking-[0.22em] font-sans">Auto-refresh every 30 seconds</p>
      </div>
    </main>
  );
}
